import { Caption } from '@casavo/base-ui'
import useTranslation from 'next-translate/useTranslation'

import { useCities } from '@/context/Cities'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { CanonicalFilter } from '@/types/filter'
import { isCityReferenceArea } from '@/utils/cities-mapper'
import { canonicalFilters } from '@/utils/get-page-type-from-params'
import { handleListingsCanonicalFiltersClick, handleListingsCanonicalZonesClick } from '@/utils/tracking/eventsHandlers'

import { linkBuilder } from '../Link/linkBuilder'

import mostVisitedZonesSlug from './mostVisitedZonesSlug'

type Props = {
  canonicalFilter: CanonicalFilter
  isLoading: boolean
}

const PrerenderedSeoLinks: React.FC<Props> = ({ canonicalFilter, isLoading }) => {
  const { lang, t } = useTranslation()
  const cities = useCities()

  const currentCity = cities.router.city || cities.router.area

  const isCurrentZone = (zone) => zone.slug === cities.router.zone?.slug
  const mostVisitedZonesSlugForCity = mostVisitedZonesSlug[currentCity.name.toUpperCase()] || []
  const zones = cities.router.city?.city_zones
    .filter((zone) => mostVisitedZonesSlugForCity.includes(zone.slug) && !isCurrentZone(zone))
    .sort((z1, z2) => mostVisitedZonesSlugForCity.indexOf(z1.slug) - mostVisitedZonesSlugForCity.indexOf(z2.slug))

  const showPrerenderedSeoLinks = isCityReferenceArea(cities.router.city) || zones?.length > 0

  if (!showPrerenderedSeoLinks) {
    return null
  }

  return (
    <div className={sprinkles({ display: isLoading ? 'none' : 'block', marginTop: '4xl' })}>
      {isCityReferenceArea(cities.router.city) && (
        <>
          <Caption strong as="h3">
            {t('common:listings.canonical-links-title', { city: cities.router.city.translatedCityName })}
          </Caption>
          <Caption>
            {canonicalFilters
              .filter((cF) => cF.id !== canonicalFilter?.id)
              .map((canonicalFilter) => {
                return (
                  <a
                    key={canonicalFilter.id}
                    className={sprinkles({
                      display: 'inline-block',
                      marginRight: 's',
                      paddingBottom: 'xs',
                    })}
                    href={`${linkBuilder.listingByCity.url(lang, cities.list, currentCity.slug)}${
                      canonicalFilter.slug[lang]
                    }/`}
                    onClick={() => handleListingsCanonicalFiltersClick(canonicalFilter.id)}
                  >
                    {t(`canonical-filters:${canonicalFilter.id}.label`)}
                  </a>
                )
              })}
          </Caption>
        </>
      )}
      {zones?.length > 0 && (
        <div className={sprinkles({ paddingTop: 'l' })}>
          <Caption strong as="h3">
            {t('common:listings.most-visited-zones-title', { city: cities.router.city.translatedCityName })}
          </Caption>

          <Caption>
            {zones.map((zone, idx, arr) => (
              <a
                key={zone.slug}
                className={sprinkles({
                  display: 'inline-block',
                  marginRight: 'xs',
                  paddingBottom: 'xs',
                })}
                href={linkBuilder.listingByZone.url(lang, cities.list, currentCity.slug, zone.slug)}
                onClick={() => handleListingsCanonicalZonesClick(zone.slug)}
              >
                {zone.label} {idx !== arr.length - 1 ? '|' : ''}
              </a>
            ))}
          </Caption>
        </div>
      )}
    </div>
  )
}

export default PrerenderedSeoLinks
